import { useEffect, useRef } from "react";
import { devLog } from "@/lib/log";

/**
 * Live waveform visualiser powered by Web Audio AnalyserNode.
 * Pass either a MediaStream (for live mic recording) or an HTMLAudioElement (for playback).
 * Falls back to a set of pulsing bars when the browser has no Web Audio support.
 */
export default function Waveform({ stream, audioEl, active = false, barCount = 32, accent = "#FF9500" }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef({ audio: null, analyser: null, source: null, raf: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx2d = canvas.getContext("2d");
    // Fit for HiDPI
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let cleanup = () => {};
    const AudioCtx = window.AudioContext || window.webkitAudioContext;

    if (!AudioCtx || (!stream && !audioEl)) {
      // Fallback pulsing bars
      let phase = 0;
      const render = () => {
        const w = canvas.clientWidth, h = canvas.clientHeight;
        ctx2d.clearRect(0, 0, w, h);
        const gap = 2;
        const bw = Math.max(2, (w - gap * (barCount - 1)) / barCount);
        for (let i = 0; i < barCount; i++) {
          const t = (i / barCount) * Math.PI * 2 + phase;
          const amp = active ? 0.15 + 0.35 * (0.5 + 0.5 * Math.sin(t)) : 0.08;
          const barH = amp * h;
          ctx2d.fillStyle = accent;
          const x = i * (bw + gap);
          const y = (h - barH) / 2;
          ctx2d.fillRect(x, y, bw, barH);
        }
        phase += active ? 0.15 : 0.03;
        ctxRef.current.raf = requestAnimationFrame(render);
      };
      render();
      cleanup = () => cancelAnimationFrame(ctxRef.current.raf);
      return () => { cleanup(); window.removeEventListener("resize", resize); };
    }

    const audio = new AudioCtx();
    const analyser = audio.createAnalyser();
    analyser.fftSize = 128;
    let source;
    try {
      if (stream) {
        source = audio.createMediaStreamSource(stream);
      } else if (audioEl) {
        source = audio.createMediaElementSource(audioEl);
        source.connect(audio.destination); // keep audible during playback
      }
      source.connect(analyser);
    } catch (e) {
      // Some browsers throw if audioEl is already connected — treat as inactive
      source = null;
    }
    ctxRef.current = { audio, analyser, source, raf: 0 };

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    const render = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      ctx2d.clearRect(0, 0, w, h);
      analyser.getByteFrequencyData(buffer);
      const gap = 2;
      const bw = Math.max(2, (w - gap * (barCount - 1)) / barCount);
      const step = Math.max(1, Math.floor(buffer.length / barCount));
      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        for (let k = 0; k < step; k++) sum += buffer[i * step + k] || 0;
        const v = sum / (step * 255);
        const amp = active ? Math.max(0.08, v) : Math.max(0.06, v * 0.4);
        const barH = amp * h;
        ctx2d.fillStyle = accent;
        const x = i * (bw + gap);
        const y = (h - barH) / 2;
        ctx2d.fillRect(x, y, bw, barH);
      }
      ctxRef.current.raf = requestAnimationFrame(render);
    };
    render();

    cleanup = () => {
      cancelAnimationFrame(ctxRef.current.raf);
      try { source?.disconnect(); } catch (err) { devLog.debug("waveform source disconnect", err); }
      try { analyser?.disconnect(); } catch (err) { devLog.debug("waveform analyser disconnect", err); }
      try { audio?.close(); } catch (err) { devLog.debug("waveform audio close", err); }
    };
    return () => { cleanup(); window.removeEventListener("resize", resize); };
  }, [stream, audioEl, active, barCount, accent]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="waveform-canvas"
      className="w-full h-full block"
      aria-hidden="true"
    />
  );
}
